import React, { useState, useEffect, useRef } from 'react'
import { X, Save, RotateCcw, RotateCw, Eraser, Pen } from 'lucide-react'
import { satsueiDataTorokuService } from '../../services/satsueiDataTorokuService'
import { useApiError } from '../../../shared/hooks/useApiError'
import MessageDialog from '../../../shared/ui/MessageDialog'
import ImageCard from '../ImageCard'
import CanvasAnnotator from '../../../lib/canvas-annotator.es.js'

const ImageHandwriteEditModal = ({ isOpen, onClose, imageList, objectId, onAnnotationComplete }) => {
  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  const [selectedImage, setSelectedImage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveOption, setSaveOption] = useState('1') // 1: increment editId, 2: new imageId
  
  // Canvas annotator state
  const baseCanvasRef = useRef(null)
  const annotRef = useRef(null)
  const [tool, setTool] = useState('pen')
  const [color, setColor] = useState('#000000')
  const [lineWidth, setLineWidth] = useState(4)
  const [eraserSize, setEraserSize] = useState(20)
  const [fit, setFit] = useState('stretch') // Changed to 'stretch' since canvas will match image size
  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 }) // Dynamic canvas size
  const pendingImageUrlRef = useRef(null) // Store image URL to load after canvas resize
  const [imageReloadTrigger, setImageReloadTrigger] = useState(0) // Force image reload
  const [imageDimensions, setImageDimensions] = useState(null) // Original image dimensions
  const canvasContainerRef = useRef(null)

  const ensure = () => {
    if (!annotRef.current) throw new Error('Annotator not ready')
    return annotRef.current
  }

  // Initialize canvas annotator when modal opens or canvas size changes
  useEffect(() => {
    if (!isOpen || !baseCanvasRef.current) return

    const annot = new CanvasAnnotator()

    annot.Init({
      canvas: baseCanvasRef.current,
      width: canvasSize.width,
      height: canvasSize.height,
      fit: fit,
      tool: tool,
      eraserSize: eraserSize,
      devicePixelRatioAware: true,
      strokeStyle: color,
      lineWidth: lineWidth,
      lineCap: 'round',
    })

    annotRef.current = annot

    // Load pending image after annotator is ready
    if (pendingImageUrlRef.current) {
      const url = pendingImageUrlRef.current
      pendingImageUrlRef.current = null
      annot.Load({ url: url })
        .catch(error => {
          console.error('Failed to load pending image:', error)
        })
    }

    return () => {
      if (annotRef.current) {
        annotRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, canvasSize.width, canvasSize.height])

  // Update annotator settings when they change
  useEffect(() => {
    if (!annotRef.current) return
    
    annotRef.current.SetFit(fit)
  }, [fit])

  useEffect(() => {
    if (!annotRef.current) return
    
    annotRef.current.SetTool(tool)
  }, [tool])

  useEffect(() => {
    if (!annotRef.current) return
    
    annotRef.current.SetStroke({ strokeStyle: color, lineWidth: lineWidth })
  }, [color, lineWidth])

  useEffect(() => {
    if (!annotRef.current) return
    
    annotRef.current.SetEraser({ size: eraserSize })
  }, [eraserSize])

  // Load image when selected and resize canvas to match image dimensions
  useEffect(() => {
    if (!selectedImage || !objectId || !isOpen) return

    const loadImage = async () => {
      setIsLoading(true)
      try {
        const response = await satsueiDataTorokuService.getImageBlobForEdit(
          selectedImage.objectId,
          selectedImage.objectImageId,
          selectedImage.objectImageEditId,
          selectedImage.filenameExtension
        )
        
        // Convert blob to URL and get image dimensions first
        const blobUrl = URL.createObjectURL(response.data)
        const img = new Image()
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            // Calculate available space - responsive for desktop and tablet
            // Modal is max-w-7xl (1280px) and h-[95vh], account for UI elements
            const isTablet = window.innerWidth < 1024
            const headerHeight = 60
            const controlsHeight = isTablet ? 200 : 250
            const leftPanelWidth = isTablet ? 0 : 320 // No left panel on tablet (stacked layout)
            const padding = isTablet ? 20 : 40
            
            const availableWidth = Math.max(300, window.innerWidth - leftPanelWidth - padding * 2)
            const availableHeight = Math.max(250, window.innerHeight - headerHeight - controlsHeight - padding * 2)
            
            // Calculate canvas size to match image, but fit within available viewport
            let canvasWidth = img.width
            let canvasHeight = img.height
            
            // Scale down to fit viewport while maintaining aspect ratio
            const scaleX = availableWidth / canvasWidth
            const scaleY = availableHeight / canvasHeight
            const scale = Math.min(scaleX, scaleY, 1) // Don't scale up, only down
            
            if (scale < 1) {
              canvasWidth = Math.round(canvasWidth * scale)
              canvasHeight = Math.round(canvasHeight * scale)
            }
            
            // Store image URL to load after canvas resizes
            pendingImageUrlRef.current = blobUrl
            
            // Update canvas size - this will trigger annotator re-initialization
            setCanvasSize({ width: canvasWidth, height: canvasHeight })
            
            resolve()
          }
          img.onerror = reject
          img.src = blobUrl
        })
        
        // Don't revoke URL immediately - let it stay for rendering
      } catch (error) {
        console.error('Failed to load image blob:', error)
        handleApiError(error, '画像の読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }

    loadImage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, objectId, isOpen, imageReloadTrigger])

  // Update tool
  const handleToolChange = (newTool) => {
    ensure().SetTool(newTool)
    setTool(newTool)
  }

  // Update color
  const handleColorChange = (newColor) => {
    ensure().SetStroke({ strokeStyle: newColor })
    setColor(newColor)
  }

  // Update line width
  const handleLineWidthChange = (newWidth) => {
    ensure().SetStroke({ lineWidth: newWidth })
    setLineWidth(newWidth)
  }

  // Update eraser size
  const handleEraserSizeChange = (newSize) => {
    ensure().SetEraser({ size: newSize })
    setEraserSize(newSize)
  }

  // Update fit mode
  const handleFitChange = (newFit) => {
    ensure().SetFit(newFit)
    setFit(newFit)
  }

  // Undo
  const handleUndo = () => {
    ensure().Undo()
  }

  // Redo
  const handleRedo = () => {
    ensure().Redo()
  }

  // Clear annotations
  const handleClear = () => {
    ensure().Clear()
  }

  // Save annotated image
  const handleSave = async () => {
    if (!selectedImage || !objectId || !annotRef.current) {
      handleApiError(new Error('画像が選択されていません'), '画像を選択してください')
      return
    }

    setIsSaving(true)
    try {
      let objectImageEditId
      let objectImageId

      if (saveOption === '1') {
        // Option 1: Keep same objectImageId, increment objectImageEditId
        const checkMaxObjectImageEditId = await satsueiDataTorokuService.getMaxImageEditId(
          selectedImage.objectId,
          selectedImage.objectImageId
        )
        objectImageEditId = checkMaxObjectImageEditId.objectImageEditId + 1
        objectImageId = selectedImage.objectImageId
      } else {
        // Option 2: Create new objectImageId with objectImageEditId as 0
        objectImageEditId = 0
        // Don't set objectImageId - let backend generate a new one
        objectImageId = null
      }

      // Save annotated image as blob
      const format = selectedImage.filenameExtension === 'png' ? 'png' : 
                     selectedImage.filenameExtension === 'jpg' || selectedImage.filenameExtension === 'jpeg' ? 'jpeg' : 'png'
      const annotatedBlob = await annotRef.current.Save({
        return: 'blob',
        format: format,
        quality: format === 'png' ? 1 : 0.92
      })

      // Convert blob to File
      const fileName = `${new Date().getTime()}.${selectedImage.filenameExtension}`
      const file = new File([annotatedBlob], fileName, { 
        type: format === 'png' ? 'image/png' : 'image/jpeg' 
      })

      // Create FormData for upload
      const formData = new FormData()
      formData.append('objectId', selectedImage.objectId)
      if (objectImageId !== null) {
        formData.append('objectImageId', objectImageId)
      }
      formData.append('inputFile', file)
      formData.append('fileName', fileName)
      formData.append('fileSize', file.size)
      formData.append('imageFolderId', selectedImage.imageFolderId)
      formData.append('objectImageEditId', objectImageEditId)

      // Upload the annotated image
      const response = await satsueiDataTorokuService.uploadEditedImage(formData)
      
      // Check if response has error
      if (handleApiResponse(response)) {
        return // Error was handled, exit early
      }
      
      // Show success message
      showSuccessMessage('注釈画像の保存が完了しました。')
      
      // Refresh image list
      onAnnotationComplete()
    } catch (error) {
      console.error('Failed to save annotated image:', error)
      handleApiError(error, '注釈画像の保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    // Clean up
    setSelectedImage(null)
    setTool('pen')
    setColor('#000000')
    setLineWidth(4)
    setEraserSize(20)
    setFit('stretch')
    setSaveOption('1')
    setCanvasSize({ width: 1200, height: 800 }) // Reset to default
    pendingImageUrlRef.current = null
    annotRef.current = null
    onClose()
  }

  const handleImageSelect = (image) => {
    // Clear any pending image URL when selecting an image
    pendingImageUrlRef.current = null
    // Clear annotations if annotator is ready
    if (annotRef.current) {
      try {
        annotRef.current.Clear()
      } catch (error) {
        // Annotator might not be ready yet, ignore
      }
    }
    // Reset canvas size and trigger image reload (even for same image)
    setCanvasSize({ width: 1200, height: 800 })
    setImageDimensions(null)
    setImageReloadTrigger(prev => prev + 1) // Force reload
    setSelectedImage(image)
  }

  // Recalculate canvas size so the image always fits inside the canvas container
  // without cropping, whenever the image dimensions or window size change.
  useEffect(() => {
    if (!imageDimensions || !canvasContainerRef.current) return

    const updateCanvasSizeToFit = () => {
      if (!canvasContainerRef.current || !imageDimensions) return

      const rect = canvasContainerRef.current.getBoundingClientRect()
      // Subtract padding from available space (matching inline style padding: 10px)
      const availableWidth = Math.max(50, rect.width - 20)
      const availableHeight = Math.max(50, rect.height - 20)

      let canvasWidth = imageDimensions.width
      let canvasHeight = imageDimensions.height

      // Scale down to fit container while preserving aspect ratio; don't scale up.
      const scaleX = availableWidth / canvasWidth
      const scaleY = availableHeight / canvasHeight
      const scale = Math.min(scaleX, scaleY, 1)

      if (scale < 1) {
        canvasWidth = Math.round(canvasWidth * scale)
        canvasHeight = Math.round(canvasHeight * scale)
      }

      setCanvasSize({ width: canvasWidth, height: canvasHeight })
    }

    // Initial calculation
    updateCanvasSizeToFit()

    // Recalculate on window resize
    window.addEventListener('resize', updateCanvasSizeToFit)
    return () => {
      window.removeEventListener('resize', updateCanvasSizeToFit)
    }
  }, [imageDimensions])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" data-testid="image-annotation-modal">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl h-[95vh] sm:h-5/6 flex flex-col" data-testid="modal-content">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">画像手書き編集</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="close-modal-button"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Left Panel - Image Selection */}
          <div className="w-full lg:w-80 p-3 sm:p-4 border-b lg:border-b-0 lg:border-r border-gray-200 overflow-y-auto" data-testid="image-selection-panel">
            <h4 className="text-sm font-medium text-gray-700 mb-3">画像選択</h4>
            <div className="grid gap-2 sm:gap-3" 
                 data-testid="image-selection-grid"
                 style={{
                   gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                   gridAutoRows: 'minmax(100px, auto)'
                 }}>
              {imageList.map((image) => {
                const isSelected = selectedImage?.objectImageId === image.objectImageId
                return (
                  <div
                    key={image.objectImageId}
                    className={`transition-all cursor-pointer flex justify-center ${
                      isSelected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                    onClick={() => handleImageSelect(image)}
                    data-testid={`image-selection-item-${image.objectImageId}`}
                  >
                    <ImageCard 
                      image={image} 
                      imageService={satsueiDataTorokuService}
                      pageType="satsueiDataToroku"
                    />
                  </div>
                )
              })}
            </div>
          </div>

          {/* Right Panel - Annotation Canvas and Controls */}
          <div className="flex-1 flex flex-col overflow-hidden" data-testid="annotation-panel">
            {/* Canvas Area */}
            <div 
              className="flex-1 overflow-hidden bg-gray-100 flex items-center justify-center" 
              ref={canvasContainerRef}
              data-testid="canvas-container" 
              style={{ position: 'relative', padding: '10px' }}
            >
              {/* Wrapper div for CanvasAnnotator - library expects canvas parent to be position: relative */}
              {/* This wrapper is centered by the parent's flexbox, and the library positions canvases absolutely within it */}
              <div
                id="canvas-wrapper"
                style={{
                  position: 'relative',
                  width: `${canvasSize.width}px`,
                  height: `${canvasSize.height}px`,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  flexShrink: 0
                }}
              >
                <canvas 
                  ref={baseCanvasRef}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  style={{
                    display: 'block',
                    border: '1px solid #ccc',
                    background: '#fff'
                  }}
                  data-testid="annotation-canvas"
                />
              </div>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75" style={{ zIndex: 20 }}>
                  <div className="text-gray-500">読み込み中...</div>
                </div>
              )}
              {!selectedImage && !isLoading && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 10 }}>
                  <div className="text-gray-500 bg-white bg-opacity-75 px-4 py-2 rounded">画像を選択してください</div>
                </div>
              )}
            </div>

            {/* Controls Panel */}
            <div className="p-3 sm:p-4 border-t border-gray-200 overflow-y-auto max-h-64" data-testid="controls-panel">
              <div className="flex flex-wrap gap-3 sm:gap-4 items-end">
                {/* Tool Selection */}
                <div data-testid="tool-selection" className="min-w-[100px]">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">ツール</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToolChange('pen')}
                      className={`flex-1 px-2 py-1.5 rounded text-xs sm:text-sm transition-colors ${
                        tool === 'pen' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      data-testid="pen-tool-button"
                    >
                      <Pen size={14} className="mx-auto" />
                    </button>
                    <button
                      onClick={() => handleToolChange('eraser')}
                      className={`flex-1 px-2 py-1.5 rounded text-xs sm:text-sm transition-colors ${
                        tool === 'eraser' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      data-testid="eraser-tool-button"
                    >
                      <Eraser size={14} className="mx-auto" />
                    </button>
                  </div>
                </div>

                {/* Color Picker */}
                {tool === 'pen' && (
                  <div data-testid="color-selection" className="flex flex-col">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">色</h4>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => handleColorChange(e.target.value)}
                      className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                      style={{ minWidth: '48px' }}
                      data-testid="color-picker"
                    />
                  </div>
                )}

                {/* Line Width */}
                {tool === 'pen' && (
                  <div data-testid="line-width-selection" className="min-w-[120px]">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">太さ: {lineWidth}px</h4>
                    <input
                      type="range"
                      min={1}
                      max={40}
                      value={lineWidth}
                      onChange={(e) => handleLineWidthChange(Number(e.target.value))}
                      className="w-full"
                      data-testid="line-width-slider"
                    />
                  </div>
                )}

                {/* Eraser Size */}
                {tool === 'eraser' && (
                  <div data-testid="eraser-size-selection" className="min-w-[120px]">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">消し幅: {eraserSize}px</h4>
                    <input
                      type="range"
                      min={5}
                      max={80}
                      value={eraserSize}
                      onChange={(e) => handleEraserSizeChange(Number(e.target.value))}
                      className="w-full"
                      data-testid="eraser-size-slider"
                    />
                  </div>
                )}

                {/* Fit Mode - Optional since canvas matches image size, but kept for flexibility */}
                <div data-testid="fit-mode-selection" className="min-w-[140px]">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">表示モード</h4>
                  <select
                    value={fit}
                    onChange={(e) => handleFitChange(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm"
                    data-testid="fit-mode-select"
                  >
                    <option value="stretch">stretch（引き伸ばし）</option>
                    <option value="contain">contain（全体）</option>
                    <option value="cover">cover（全面）</option>
                  </select>
                </div>

                {/* Undo/Redo/Clear */}
                <div data-testid="action-buttons" className="min-w-[120px]">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">操作</h4>
                  <div className="flex space-x-1">
                    <button
                      onClick={handleUndo}
                      className="flex-1 px-2 py-1.5 bg-gray-200 text-gray-700 rounded text-xs sm:text-sm hover:bg-gray-300 transition-colors"
                      data-testid="undo-button"
                      title="Undo"
                    >
                      <RotateCcw size={14} className="mx-auto" />
                    </button>
                    <button
                      onClick={handleRedo}
                      className="flex-1 px-2 py-1.5 bg-gray-200 text-gray-700 rounded text-xs sm:text-sm hover:bg-gray-300 transition-colors"
                      data-testid="redo-button"
                      title="Redo"
                    >
                      <RotateCw size={14} className="mx-auto" />
                    </button>
                    <button
                      onClick={handleClear}
                      className="flex-1 px-2 py-1.5 bg-red-200 text-red-700 rounded text-xs sm:text-sm hover:bg-red-300 transition-colors"
                      data-testid="clear-button"
                      title="Clear"
                    >
                      <X size={14} className="mx-auto" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Save Option */}
              <div className="mt-4" data-testid="save-option-selection">
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">保存オプション</h4>
                <select
                  value={saveOption}
                  onChange={(e) => setSaveOption(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm"
                  data-testid="save-option-select"
                >
                  <option value="1">元画像を上書きして保存</option>
                  <option value="2">新しい画像として保存</option>
                </select>
              </div>

              {/* Save Button */}
              <div className="mt-4">
                <button
                  onClick={handleSave}
                  disabled={isSaving || !selectedImage || isLoading}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  data-testid="save-button"
                >
                  <Save size={16} />
                  <span>{isSaving ? '保存中...' : '保存'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={
          messageType === 'error' ? 'エラー' :
          messageType === 'success' ? '成功' :
          messageType === 'warning' ? '警告' :
          '情報'
        }
        message={errorMessage}
        type={messageType}
        buttonText="閉じる"
        data-testid="message-dialog"
      />
    </div>
  )
}

export default ImageHandwriteEditModal

